import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { InvoicesDataViewContext } from "@insite/client-framework/Store/Data/Invoices/InvoicesSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateSearchFields from "@insite/client-framework/Store/Pages/InvoiceHistory/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InvoiceDetailPageTypeLink from "@insite/content-library/Components/InvoiceDetailPageTypeLink";
import Button, { ButtonWrapper } from "@insite/mobius/Button";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import DataTable, { DataTableProps, SortOrderOptions } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import CreditCard from "@insite/mobius/Icons/CreditCard";
import Pocket from "@insite/mobius/Icons/Pocket";
import Printer from "@insite/mobius/Icons/Printer";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import generatePdf from "@tSurya/Handlers/GeneratePdfHandler";
import CustomPayNowIcon from "@tSurya/Icons/CustomPayNowIcon";
import CustomReturnIcon from "@tSurya/Icons/CustomReturnIcon";
import PlusIcon from "@tSurya/Icons/PlusIcon";
import { TYPOGRAPHY } from "@tSurya/theme/design";
import * as React from "react";
import { useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showInvoiceNumber = "showInvoiceNumber",
    showInvoiceDate = "showInvoiceDate",
    showTerms = "showTerms",
    showDueDate = "showDueDate",
    showShipTo = "showShipTo",
    showPONumber = "showPONumber",
    showStatus = "showStatus",
    showInvoiceTotal = "showInvoiceTotal",
    showCurrentBalance = "showCurrentBalance",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showInvoiceNumber]: boolean;
        [fields.showInvoiceDate]: boolean;
        [fields.showTerms]: boolean;
        [fields.showDueDate]: boolean;
        [fields.showShipTo]: boolean;
        [fields.showPONumber]: boolean;
        [fields.showStatus]: boolean;
        [fields.showInvoiceTotal]: boolean;
        [fields.showCurrentBalance]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    getInvoicesParameter: state.pages.invoiceHistory.getInvoicesParameter,
    failedToLoadInvoices: state.data.invoices.failedToLoadInvoices,
    language: state.context.session.language,
    rmaLink: getPageLinkByPageType(state, "RequestRmaPage"),
    showPricing: state.context.showPricing,
});

const mapDispatchToProps = {
    updateSearchFields,
    generatePdf,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface InvoiceHistoryTableStyles {
    container?: InjectableCss;
    headerClickables?: ClickableProps;
    headerText?: TypographyProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableHeaders?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    dataTableCells?: DataTableCellBaseProps;
    invoiceNumberHeader?: DataTableHeaderProps;
    invoiceDateHeader?: DataTableHeaderProps;
    termsHeader?: DataTableHeaderProps;
    dueDateHeader?: DataTableHeaderProps;
    shipToHeader?: DataTableHeaderProps;
    poNumberHeader?: DataTableHeaderProps;
    statusHeader?: DataTableHeaderProps;
    totalHeader?: DataTableHeaderProps;
    currentBalanceHeader?: DataTableHeaderProps;
    invoiceNumberCell?: DataTableCellBaseProps;
    invoiceDateCell?: DataTableCellBaseProps;
    termsCell?: DataTableCellBaseProps;
    dueDateCell?: DataTableCellBaseProps;
    shipToCell?: DataTableCellBaseProps;
    poNumberCell?: DataTableCellBaseProps;
    statusCell?: DataTableCellBaseProps;
    link?: LinkPresentationProps;
    totalCell?: DataTableCellBaseProps;
    currentBalanceCell?: DataTableCellBaseProps;
    returnorderStyles?: DataTableCellBaseProps;
    button?: InjectableCss;
    printIcon?: IconPresentationProps;
    returnIcon?: IconPresentationProps;
    payNowIcon?: IconPresentationProps;
}

export const tableStyles: InvoiceHistoryTableStyles = {
    printIcon: {
        css: css`
            height: 19.25px;
            width: 22px;
            > svg {
                width: 100%;
                height: 100%;
            }
        `,
    },
    returnIcon: {
        css: css`
            height: 16.5px;
            width: 20.62px;
            > svg {
                width: 100%;
                height: 100%;
            }
        `,
    },
    payNowIcon: {
        css: css`
            width: 21px;
            height: 15px;
            > svg {
                width: 100%;
                height: 100%;
            }
        `,
    },
    dataTableRow: {
        css: css`
            &:nth-child(even) {
                background: #ffffff !important;
            }
        `,
    },
    container: {
        css: css`
            overflow: auto;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    noResultsContainer: {
        css: css`
            text-align: center;
            padding: 20px;
        `,
    },
    noResultsText: {
        size: 16,
        weight: 600,
    },
    headerClickables: {
        css: css`
            &:focus {
                outline-style: none;
            }
            width: 100%;
            justify-content: space-between;
            display: block;
        `, // TODO ISC-12604 focus removal is incorrect
    },
    totalHeader: {
        alignX: "right",
    },
    currentBalanceHeader: {
        alignX: "right",
    },
    totalCell: {
        alignX: "right",
        css: css`
            text-align: right !important;
        `,
    },
    currentBalanceCell: {
        alignX: "right",
        css: css`
            text-align: right !important;
        `,
    },
    returnorderStyles: {
        css: css`
            [class^="StyledButton"] {
                justify-content: center;
            }
            button {
                &:hover {
                    background: ${getColor("text.accent")};
                    span[class^="TypographyStyle"] {
                        color: ${getColor("secondary")};
                    }
                }
            }
        `,
    },
    link: {
        css: css`
            span {
                font-size: 15px;
                color: ${getColor("text.link")} !important;
                &:hover {
                    text-align: center;
                    text-decoration-line: underline;
                }
            }
        `,
    },
    button: {
        css: css`
            display: flex;
            align-items: center;
            padding: 0 36px;
            text-align: center;
            gap: 15px;
            border: 2px solid ${getColor("text.accent")};
            border: none;
            background-color: ${getColor("primary.contrast")};
            &:hover {
                background-color: #ffffff !important;
            }
            &:disabled {
                background-color: #ffffff !important;
            }
            span {
                font-family: ${TYPOGRAPHY.fontFamily.title};
                font-style: normal;
                font-weight: ${TYPOGRAPHY.weight.bold};
                font-size: ${TYPOGRAPHY.size.smaller};
                line-height: ${TYPOGRAPHY.lineHeight.xbase};
                color: ${getColor("text.main")};
                text-align: center;
                background-color: ${getColor("primary.contrast")};
            }
        `,
    },
    dataTable: {
        css: css`
            border: 1px solid ${getColor("common.border")};
            tr {
                > th {
                    width: 124px !important;
                    box-sizing: border-box;
                    height: 60px;
                    white-space: wrap !important;
                    padding: 0px;
                    min-width: 124px !important;
                    &.printColumn {
                        width: 59px !important;
                        padding: 0px;
                        gap: 0px;
                        min-width: 59px !important;
                        &:last-child {
                            span[class^="IconWrapper"] {
                                display: none;
                            }
                        }
                    }
                }
            }

            tr {
                > td {
                    width: 123px !important;
                    box-sizing: border-box;
                    height: 54px;
                    &.printColumn {
                        width: 59px !important;
                        padding: 0px !important;
                        > button {
                            padding: 0px !important;
                            gap: 0px;
                            width: 100%;
                            justify-content: center;
                        }
                    }
                    span {
                        white-space: normal;
                    }
                }
            }
        `,
    },
};

const styles = tableStyles;

const InvoiceHistoryTable = (props: Props) => {
    const headerClick = (sortField: string) => {
        const sort = props.getInvoicesParameter.sort === sortField ? `${sortField} DESC` : sortField;
        props.updateSearchFields({ sort });
    };

    const sorted = (sortField: string) => {
        let sorted: boolean | string = false;
        if (props.getInvoicesParameter.sort === sortField) {
            sorted = "ascending";
        } else if (props.getInvoicesParameter.sort === `${sortField} DESC`) {
            sorted = "descending";
        }
        return sorted as SortOrderOptions;
    };

    if (props.failedToLoadInvoices) {
        return null;
    }

    const invoicesDataView = useContext(InvoicesDataViewContext);
    const toasterContext = useContext(ToasterContext);

    const setButtonAttribute = (event: any) => {
        if (event.target.tagName === "SPAN") {
            event.target.parentElement.setAttribute("disabled", "");
        } else {
            event.target.setAttribute("disabled", "");
        }
    };

    const removeButtonAttribute = (event: any) => {
        if (event.target.tagName === "SPAN") {
            event.target.parentElement.removeAttribute("disabled", "");
        } else {
            event.target.removeAttribute("disabled", "");
        }
    };
    const downloadTearsheetClickHandler = (invoiceNumber: string, event: any) => {
        if (typeof window !== undefined) {
            setButtonAttribute(event);
            props.generatePdf({
                identifier: invoiceNumber,
                templateName: "invoice",
                onSuccess(result:any) {
                    removeButtonAttribute(event);
                },
                onError(result:any) {
                    toasterContext.addToast({
                        body: translate(`${result}`),
                        messageType: "danger",
                    });
                    removeButtonAttribute(event);
                },
            });
        }
    };

    if (!invoicesDataView.value) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner}></LoadingSpinner>
            </StyledWrapper>
        );
    }

    if (invoicesDataView.value.length === 0) {
        return (
            <StyledWrapper {...styles.noResultsContainer}>
                <Typography {...styles.noResultsText}>{translate("No invoices found")}</Typography>
            </StyledWrapper>
        );
    }

    const rows = invoicesDataView.value.map(invoice => {
        return {
            id: invoice.id,
            invoiceNumber: invoice.invoiceNumber,
            date: invoice.invoiceDate
                ? new Date(invoice.invoiceDate).toLocaleDateString("en-US", {
                      month: "2-digit",
                      day: "2-digit",
                      year: "numeric",
                  })
                : "",
            terms: invoice.terms,
            dueDate: invoice.dueDate
                ? new Date(invoice.dueDate).toLocaleDateString("en-US", {
                      month: "2-digit",
                      day: "2-digit",
                      year: "numeric",
                  })
                : "",
            shipTo: `${invoice.stCompanyName} ${invoice.btAddress1} ${invoice.btAddress2} ${invoice.shipToCity} ${invoice.shipToState}`,
            customerPO: invoice.customerPO,
            status: invoice.status,
            total: invoice.invoiceTotal,
            totalDisplay: invoice.invoiceTotalDisplay,
            currentBalance: invoice.currentBalance,
            currentBalanceDisplay: invoice.currentBalanceDisplay,
            orderNumber: invoice.properties.erpOrderNo,
        };
    });

    const {
        showInvoiceNumber,
        showInvoiceDate,
        showTerms,
        showDueDate,
        showShipTo,
        showPONumber,
        showStatus,
        showInvoiceTotal,
        showCurrentBalance,
    } = props.fields;

    const isTrueOrUndefined = (value?: boolean) => value === true || value === undefined;

    return (
        <StyledWrapper {...styles.container}>
            <DataTable {...styles.dataTable}>
                <DataTableHead {...styles.dataTableHead}>
                    {isTrueOrUndefined(showInvoiceNumber) && (
                        <DataTableHeader
                            tight
                            sorted={sorted("invoiceNumber")}
                            {...styles.invoiceNumberHeader}
                            onSortClick={() => headerClick("invoiceNumber")}
                        >
                            {translate("Invoice #")}
                        </DataTableHeader>
                    )}
                    {isTrueOrUndefined(showInvoiceDate) && (
                        <DataTableHeader
                            tight
                            sorted={sorted("invoiceDate")}
                            {...styles.invoiceDateHeader}
                            onSortClick={() => headerClick("invoiceDate")}
                            data-test-selector="invoiceHistory_invoiceHeader_date"
                        >
                            {translate("Invoice Date")}
                        </DataTableHeader>
                    )}
                    {/*  {isTrueOrUndefined(showTerms) && (
                        <DataTableHeader
                            tight
                            sorted={sorted("terms")}
                            {...styles.termsHeader}
                            onSortClick={() => headerClick("terms")}
                        >
                            {translate("Terms")}
                        </DataTableHeader>
                    )} */}
                    {isTrueOrUndefined(showDueDate) && (
                        <DataTableHeader
                            tight
                            sorted={sorted("dueDate")}
                            {...styles.dueDateHeader}
                            onSortClick={() => headerClick("dueDate")}
                        >
                            {translate("Due Date")}
                        </DataTableHeader>
                    )}
                    {/* {isTrueOrUndefined(showShipTo) && (
                        <DataTableHeader
                            tight
                            sorted={sorted("stCompanyName")}
                            {...styles.shipToHeader}
                            onSortClick={() => headerClick("stCompanyName")}
                        >
                            {translate("Ship To / Pick Up")}
                        </DataTableHeader>
                    )} */}
                    {isTrueOrUndefined(showPONumber) && (
                        <DataTableHeader
                            tight
                            sorted={sorted("customerPO")}
                            {...styles.poNumberHeader}
                            onSortClick={() => headerClick("customerPO")}
                        >
                            {translate("PO #")}
                        </DataTableHeader>
                    )}

                    {isTrueOrUndefined(showInvoiceTotal) && props.showPricing && (
                        <DataTableHeader
                            tight
                            sorted={sorted("invoiceTotal")}
                            {...styles.totalHeader}
                            onSortClick={() => headerClick("invoiceTotal")}
                        >
                            {translate("Invoice Total")}
                        </DataTableHeader>
                    )}
                    {isTrueOrUndefined(showCurrentBalance) && props.showPricing && (
                        <DataTableHeader
                            tight
                            sorted={sorted("currentBalance")}
                            {...styles.currentBalanceHeader}
                            onSortClick={() => headerClick("currentBalance")}
                        >
                            {translate("Current Balance")}
                        </DataTableHeader>
                    )}
                    {isTrueOrUndefined(showStatus) && (
                        <DataTableHeader
                            tight
                            sorted={sorted("status")}
                            {...styles.statusHeader}
                            onSortClick={() => headerClick("status")}
                        >
                            {translate("Status")}
                        </DataTableHeader>
                    )}
                    <DataTableHeader tight className="printColumn">
                        {translate("Print")}
                    </DataTableHeader>
                    <DataTableHeader className="printColumn" tight>
                        {translate("Return")}
                    </DataTableHeader>
                    <DataTableHeader
                        className="printColumn"
                        tight
                        sorted={sorted("status")}
                        {...styles.statusHeader}
                        onSortClick={() => headerClick("status")}
                    >
                        {translate("Pay")}
                    </DataTableHeader>
                </DataTableHead>
                <DataTableBody {...styles.dataTableBody}>
                    {rows.map(
                        (
                            {
                                id,
                                invoiceNumber,
                                date,
                                terms,
                                dueDate,
                                shipTo,
                                customerPO,
                                status,
                                total,
                                totalDisplay,
                                currentBalance,
                                currentBalanceDisplay,
                                orderNumber,
                            },
                            index,
                        ) => (
                            <DataTableRow
                                key={id}
                                {...styles.dataTableRow}
                                data-test-selector="invoiceHistory_invoiceLine"
                                data-test-key={id}
                            >
                                {isTrueOrUndefined(showInvoiceNumber) && (
                                    <DataTableCell
                                        {...styles.invoiceNumberCell}
                                        data-test-selector="invoiceHistory_invoiceLine_number"
                                    >
                                        <InvoiceDetailPageTypeLink
                                            title={invoiceNumber}
                                            invoiceNumber={invoiceNumber}
                                        />
                                    </DataTableCell>
                                )}
                                {isTrueOrUndefined(showInvoiceDate) && (
                                    <DataTableCell
                                        {...styles.invoiceDateCell}
                                        data-test-selector="invoiceHistory_invoiceLine_date"
                                    >
                                        {date}
                                    </DataTableCell>
                                )}
                                {/* {isTrueOrUndefined(showTerms) && (
                                    <DataTableCell {...styles.termsCell}>{terms}</DataTableCell>
                                )} */}
                                {isTrueOrUndefined(showDueDate) && (
                                    <DataTableCell {...styles.dueDateCell}>{dueDate}</DataTableCell>
                                )}
                                {/* {isTrueOrUndefined(showShipTo) && (
                                    <DataTableCell {...styles.shipToCell}>{shipTo}</DataTableCell>
                                )} */}
                                {isTrueOrUndefined(showPONumber) && (
                                    <DataTableCell {...styles.poNumberCell}>{customerPO}</DataTableCell>
                                )}

                                {isTrueOrUndefined(showInvoiceTotal) && props.showPricing && (
                                    <DataTableCell {...styles.totalCell}>
                                        {String(total).includes("-")
                                            ? `$(${Math.abs(total)
                                                .toFixed(2) 
                                                .replace(/\B(?=(\d{3})+(?!\d))/g, ",")})`
                                            : totalDisplay}
                                    </DataTableCell>
                                )}
                                {isTrueOrUndefined(showCurrentBalance) && props.showPricing && (
                                    <DataTableCell {...styles.currentBalanceCell}>
                                        {String(currentBalance).includes("-")
                                            ? `$(${Math.abs(currentBalance)
                                                .toFixed(2) 
                                                .replace(/\B(?=(\d{3})+(?!\d))/g, ",")})`
                                            : currentBalanceDisplay}
                                    </DataTableCell>
                                )}
                                {isTrueOrUndefined(showStatus) && (
                                    <DataTableCell {...styles.statusCell}>{status}</DataTableCell>
                                )}
                                <DataTableCell className="printColumn">
                                    <Button
                                        {...styles.button}
                                        onClick={event => downloadTearsheetClickHandler(invoiceNumber, event)}
                                    >
                                        <StyledWrapper>
                                            <Icon src={Printer} {...styles.printIcon} />
                                        </StyledWrapper>
                                    </Button>
                                </DataTableCell>

                                <DataTableCell {...styles.returnorderStyles} className="printColumn">
                                    <Clickable
                                        href={`${props.rmaLink?.url}?orderNumber=${orderNumber}&invoiceNumber=${invoiceNumber}&invoiceDate=${date}&PoNumber=${customerPO}`}
                                    >
                                        <Icon src={CustomReturnIcon} {...styles.returnIcon} />
                                    </Clickable>
                                </DataTableCell>

                                <DataTableCell {...styles.returnorderStyles} className="printColumn">
                                    {status.toLocaleUpperCase() === "PAST DUE" ||
                                    status.toLocaleUpperCase() === "UNPAID" ? (
                                        <Clickable
                                            href={`/MyAccount/Invoices/Payment?id=${invoiceNumber}&Balance=${currentBalance}&formattedBalance=${currentBalanceDisplay}`}
                                        >
                                            <Icon src={CustomPayNowIcon} {...styles.payNowIcon} />
                                        </Clickable>
                                    ) : (
                                        ""
                                    )}
                                </DataTableCell>
                            </DataTableRow>
                        ),
                    )}
                </DataTableBody>
            </DataTable>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(InvoiceHistoryTable),
    definition: {
        group: "Invoice History",
        displayName: "Search Results Table",
        allowedContexts: ["InvoiceHistoryPage"],
        fieldDefinitions: [
            {
                name: fields.showInvoiceNumber,
                displayName: "Invoice #",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "General",
                sortOrder: 0,
            },
            {
                name: fields.showInvoiceDate,
                displayName: "Invoice Date",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.showTerms,
                displayName: "Terms",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.showDueDate,
                displayName: "Due Date",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showShipTo,
                displayName: "Ship To/Pick Up",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showPONumber,
                displayName: "PO #",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.showStatus,
                displayName: "Status",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 6,
            },
            {
                name: fields.showInvoiceTotal,
                displayName: "Invoice Total",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 7,
            },
            {
                name: fields.showCurrentBalance,
                displayName: "Current Balance",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 8,
            },
        ],
    },
};

export default widgetModule;
